import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { getAvatarUrl } from "@/utils/storageUtils";

type VacationRequest = Tables<"vacation_requests">;
type Profile = Tables<"profiles">;

type RequestWithProfile = VacationRequest & {
  profile: Pick<Profile, "full_name" | "avatar_url">;
};

export function PendingRequestsTable() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});

  const { data: pendingRequests } = useQuery({
    queryKey: ["pending_requests"],
    queryFn: async () => {
      const { data: requests, error } = await supabase
        .from("vacation_requests")
        .select("*")
        .eq("status", "pending");

      if (error) throw error;

      const requestsWithProfiles = await Promise.all(
        requests.map(async (request) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("full_name, avatar_url")
            .eq("id", request.user_id)
            .single();

          return {
            ...request,
            profile,
          };
        })
      );

      return requestsWithProfiles as RequestWithProfile[];
    },
  });

  useEffect(() => {
    if (pendingRequests) {
      const newAvatarUrls: Record<string, string> = {};
      
      pendingRequests.forEach(request => {
        if (request.profile?.avatar_url) {
          newAvatarUrls[request.user_id] = getAvatarUrl(request.profile.avatar_url);
        }
      });
      
      setAvatarUrls(newAvatarUrls);
    }
  }, [pendingRequests]);

  const handleAction = async (requestId: string, action: string, comment?: string) => {
    try {
      const { data: request } = await supabase
        .from("vacation_requests")
        .select("*, profile:profiles(full_name)")
        .eq("id", requestId)
        .single();

      if (!request) {
        throw new Error("Request not found");
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Admin not found");

      const { data: adminProfile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      const { error: updateError } = await supabase
        .from("vacation_requests")
        .update({
          status: action,
          manager_comment: comment,
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (updateError) throw updateError;

      const notificationPayload = {
        userId: request.user_id,
        vacationStart: format(new Date(request.start_date), "MMM d, yyyy"),
        vacationEnd: format(new Date(request.end_date), "MMM d, yyyy"),
        reason: request.reason,
        isHalfDay: request.is_half_day,
        approved: action === "approved",
        managerComment: comment,
        adminName: adminProfile?.full_name || "Admin",
      };

      const { error: notificationError } = await supabase.functions.invoke(
        "send-admin-vacation-decision-notification",
        {
          body: notificationPayload,
        }
      );

      if (notificationError) {
        console.error("Notification error:", notificationError);
        toast({
          title: `Request ${action}`,
          description: "Status updated but notification email failed to send",
          variant: "destructive",
        });
      } else {
        toast({
          title: `Request ${action} successfully`,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["pending_requests"] });
    } catch (error) {
      console.error("Action error:", error);
      toast({
        title: "Error updating request",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="overflow-auto">
      <div className="min-w-full">
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingRequests?.map((request) => (
                <TableRow key={request.id} className="group hover:bg-muted/50 transition-colors duration-200">
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-8 w-8 border-2 border-white/30 group-hover:scale-110 transition-transform duration-200">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#D3E4FD] to-[#F1F0FB] rounded-full" />
                        <AvatarImage 
                          src={avatarUrls[request.user_id] || undefined}
                          className="object-cover relative z-10"
                          alt={request.profile?.full_name || 'User avatar'}
                        />
                        <AvatarFallback className="text-xs text-gray-700 relative z-10">
                          {request.profile?.full_name?.split(" ").map((n) => n[0]).join("") || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium group-hover:text-primary transition-colors duration-200">
                        {request.profile?.full_name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(request.start_date), "MMM d, yyyy")} -{" "}
                    {format(new Date(request.end_date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>{request.reason}</TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleAction(request.id, "approved")}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 group"
                        title="Approve request"
                      >
                        <CheckCircle2 className="h-6 w-6 transform group-hover:scale-110 transition-all duration-200" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleAction(request.id, "rejected")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 group"
                        title="Reject request"
                      >
                        <XCircle className="h-6 w-6 transform group-hover:scale-110 transition-all duration-200" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="md:hidden space-y-4">
          {pendingRequests?.map((request) => (
            <Card key={request.id} className="p-4 space-y-4 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10 border-2 border-white/30">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D3E4FD] to-[#F1F0FB] rounded-full" />
                  <AvatarImage 
                    src={avatarUrls[request.user_id] || undefined}
                    className="object-cover relative z-10"
                    alt={request.profile?.full_name || 'User avatar'}
                  />
                  <AvatarFallback className="text-sm text-gray-700 relative z-10">
                    {request.profile?.full_name?.split(" ").map((n) => n[0]).join("") || "?"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">{request.profile?.full_name}</div>
                  <div className="text-sm text-gray-500">
                    {format(new Date(request.start_date), "MMM d, yyyy")} -{" "}
                    {format(new Date(request.end_date), "MMM d, yyyy")}
                  </div>
                </div>
              </div>
              <div className="text-sm">{request.reason}</div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  className="flex-1 text-green-600 hover:text-green-700 hover:bg-green-50 group"
                  onClick={() => handleAction(request.id, "approved")}
                >
                  <CheckCircle2 className="h-6 w-6 mr-2 transform group-hover:scale-110 transition-all duration-200" />
                  Approve
                </Button>
                <Button
                  variant="ghost"
                  className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 group"
                  onClick={() => handleAction(request.id, "rejected")}
                >
                  <XCircle className="h-6 w-6 mr-2 transform group-hover:scale-110 transition-all duration-200" />
                  Reject
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
